export type VinculoOrigem = 'EXPLICITO' | 'AGENDAMENTO' | 'PIPELINE' | 'SEM_VINCULO'
export type Confianca = 'alta' | 'media' | 'baixa'

export interface VinculoResolvido {
  relacionadorId: string | null
  origem: VinculoOrigem
  confianca: Confianca
  contratoId: string
  descricao: string
  valorBem?: number
  createdAt?: string
}

interface ContratoComDados {
  id: string
  cliente_id: string
  relacionador_id: string | null
  pipeline_card_id?: string | null
}

interface Agendamento {
  id: string
  cliente_id: string
  relacionador_id: string
}

interface PipelineCard {
  id: string
  responsavel_id: string
}

interface Consultor {
  id: string
  user_id: string
  nome: string
}

export class VinculoResolver {
  /**
   * Resolve o relacionador de um contrato usando várias fontes em ordem de confiança.
   *
   * Cadeia de resolução:
   * 1. EXPLICITO: contrato.relacionador_id está preenchido
   * 2. AGENDAMENTO: busca relacionador do agendamento do cliente
   * 3. PIPELINE: resolve via pipeline_cards.responsavel_id → consultores.user_id
   * 4. SEM_VINCULO: nenhuma fonte disponível
   */
  static async resolver(
    contrato: ContratoComDados,
    agendamentos: Agendamento[],
    pipelineCards: Map<string, PipelineCard>,
    consultores: Map<string, Consultor>
  ): Promise<VinculoResolvido> {
    // 1. EXPLICITO - relacionador_id preenchido no contrato
    if (contrato.relacionador_id) {
      return {
        relacionadorId: contrato.relacionador_id,
        origem: 'EXPLICITO',
        confianca: 'alta',
        contratoId: contrato.id,
        descricao: 'Vínculo explícito no contrato',
      }
    }

    // 2. AGENDAMENTO - buscar agendamento do cliente
    const agendamentoCliente = agendamentos.find(
      a => a.cliente_id === contrato.cliente_id && a.relacionador_id
    )

    if (agendamentoCliente?.relacionador_id) {
      return {
        relacionadorId: agendamentoCliente.relacionador_id,
        origem: 'AGENDAMENTO',
        confianca: 'media',
        contratoId: contrato.id,
        descricao: `Resolvido via agendamento do cliente (${agendamentoCliente.id})`,
      }
    }

    // 3. PIPELINE - resolver via pipeline_cards.responsavel_id
    if (contrato.pipeline_card_id) {
      const pipelineCard = pipelineCards.get(contrato.pipeline_card_id)
      if (pipelineCard?.responsavel_id) {
        const consultor = consultores.get(pipelineCard.responsavel_id)
        if (consultor?.user_id) {
          return {
            relacionadorId: consultor.user_id,
            origem: 'PIPELINE',
            confianca: 'baixa',
            contratoId: contrato.id,
            descricao: `Resolvido via pipeline (${consultor.nome})`,
          }
        }
      }
    }

    // 4. SEM_VINCULO
    return {
      relacionadorId: null,
      origem: 'SEM_VINCULO',
      confianca: 'baixa',
      contratoId: contrato.id,
      descricao: 'Nenhuma fonte de vínculo encontrada',
    }
  }

  /**
   * Resolve múltiplos contratos em batch
   */
  static async resolverBatch(
    contratos: ContratoComDados[],
    agendamentos: Agendamento[],
    pipelineCards: Map<string, PipelineCard>,
    consultores: Map<string, Consultor>
  ): Promise<VinculoResolvido[]> {
    const results = await Promise.all(
      contratos.map(contrato =>
        this.resolver(contrato, agendamentos, pipelineCards, consultores)
      )
    )

    for (let i = 0; i < contratos.length; i++) {
      const extra = contratos[i] as any
      if (extra.valor_bem !== undefined) {
        results[i].valorBem = extra.valor_bem
      }
      if (extra.created_at !== undefined) {
        results[i].createdAt = extra.created_at
      }
    }

    return results
  }

  /**
   * Agrupa resultados por origem e confiança
   */
  static agruparPorOrigem(vinculos: VinculoResolvido[]) {
    return {
      total: vinculos.length,
      explicito: vinculos.filter(v => v.origem === 'EXPLICITO').length,
      agendamento: vinculos.filter(v => v.origem === 'AGENDAMENTO').length,
      pipeline: vinculos.filter(v => v.origem === 'PIPELINE').length,
      semVinculo: vinculos.filter(v => v.origem === 'SEM_VINCULO').length,
    }
  }
}
